import React, { useState, useEffect } from 'react'
import {
  Box,
  Frame,
  Typography,
  Loader,
  FlagDialog,
  ImproveDialog,
  FreelancerProfileRecommendations,
  FreelancerProfileCalendarSection,
  FreelancerProfileContactSection,
  FreelancerExpertiseSection,
  FreelancerEquipmentSection,
  FreelancerCreditsSection,
  FreelancerCommitmentSection,
  Button,
  FreelancerWorkStatusSection,
  FreelancerProfileWLCalendarSection,
  FreelancerDiversitySection,
  FreelancerRolesSection,
  FreelancerDisabilitySection,
  FreelancerAlumniSection,
  FreelancerSelfIdentificationSection
} from 'components'
import {
  useParams,
  getUserByEmail,
  getUserRecommendations,
  useHasActiveSubscription,
  useContent,
  sendImproveProfileEmail,
  useOnboardConfig,
} from 'utils'
import { Helmet } from 'react-helmet'
import { Redirect } from 'react-router'
import FreelancerProfileNotesSection from 'components/freelancer/FreelancerProfileNotesSection'
import { 
  CUSTOM_STATIC_LANDER, 
  AVAILABLE_FROM_CALENDAR, 
  DISABLE_AVAILABILITY, 
  DISABLE_CRRAL_CALENDAR,
  DIVERSITY_TAGS_ENABLED,
  GENDER_TAGS_ENABLED
} from 'constants'

// Helper function to check if onboardConfig includes an object with a specific name
const hasConfigItem = (config, itemName) => {
  // Handle case when config is an array (old style)
  if (config && Array.isArray(config)) {
    return config.some(item => item.name === itemName && item.enabled !== false);
  }
  // Handle case when config is an object with onboardConfig property (new style)
  else if (config && Array.isArray(config.onboardConfig)) {
    return config.onboardConfig.some(item => item.name === itemName && item.enabled !== false);
  }
  // Default fallback - always enable for development if config is not loaded properly
  return true;
};

const FreelancerShowView = ({ setSnackMessage, user }) => {
  const { userEmail } = useParams()
  const [foundUser, setFoundUser] = useState()
  const [flagDialogOpen, setFlagDialogOpen] = useState(false)
  const [improveDialogOpen, setImproveDialogOpen] = useState(false)
  const { title } = useContent()
  const onboardConfig = useOnboardConfig()

  useEffect(() => {
    if (userEmail) {
      getUserByEmail(userEmail).then((user) => {
        getUserRecommendations(user).then((recommendations) => {
          setFoundUser({ ...user, recommendations })
        })
      })
    }
  }, [userEmail])
  
  // Debug onboardConfig
  useEffect(() => {
    console.log('onboardConfig:', onboardConfig);
  }, [onboardConfig])

  const { hasActiveSubscription, isLoading } = useHasActiveSubscription(
    user?.id,
  )

  if (!isLoading && !hasActiveSubscription) {
    return <Redirect to="/packages" />
  }

  if (!foundUser) {
    return (
      <>
        <Helmet>
          <title>{title} - {userEmail}</title>
        </Helmet>
        <Loader />
      </>
    )
  }

  const onFlag = () => setFlagDialogOpen(true)
  const onImprove = () => setImproveDialogOpen(true)

  return (
    <Frame py={2.5} maxWidth="lg" user={user}>
      <Helmet>
        <title>
          {title} - {foundUser.firstName} {foundUser.lastName}
        </title>
      </Helmet>

      <FlagDialog
        user={user}
        flaggedUser={foundUser}
        onSubmit={() => {
          setSnackMessage('Thank you, we have flagged the user.')
          setFlagDialogOpen(false)
        }}
        onClose={() => setFlagDialogOpen(false)}
        open={flagDialogOpen}
      />

      <ImproveDialog
        user={user}
        flaggedUser={foundUser}
        onSubmit={!CUSTOM_STATIC_LANDER ? () => {
          setSnackMessage('Thank you, we have notified the user.')
          setFlagDialogOpen(false)
        } :
          (data) => sendImproveProfileEmail({
            to: foundUser.email,
            firstName: foundUser.firstName, 
            missingDetails: data,
          }).then(res => {
            setSnackMessage('Thank you, we have notified the user.')
            setImproveDialogOpen(false)
          }).catch(err => {
            console.log(data)
            setSnackMessage('Something went wrong. Please try again later.')
          })}
        onClose={() => setImproveDialogOpen(false)}
        open={improveDialogOpen}
      />

      {user ? (
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }}>
          <Box flex={1} mr={2.5}>
            <FreelancerProfileContactSection freelancer={foundUser} />
            
            {/* Diversity Tags Section */}
            <FreelancerDiversitySection freelancer={foundUser} />
            
            {/* Self Identification Section */}
            <FreelancerSelfIdentificationSection freelancer={foundUser} />
            
            {/* Disability Section */}
            <FreelancerDisabilitySection freelancer={foundUser} />
            
            {/* Roles Section */}
            <FreelancerRolesSection freelancer={foundUser} />
            
            {/* Expertise Section */}
            <FreelancerExpertiseSection freelancer={foundUser} />
            
            {/* Credits Section */}
            <FreelancerCreditsSection freelancer={foundUser} />
            
            {/* Equipment Section */}
            <FreelancerEquipmentSection freelancer={foundUser} />
            
            {/* Profile Recommendations */}
            <FreelancerProfileRecommendations freelancer={foundUser} />
          </Box>

          <Box>
            <FreelancerProfileNotesSection freelancer={foundUser} />
            
            {/* Alumni Section */}
            <FreelancerAlumniSection freelancer={foundUser} />

            <Box display="flex" flexDirection="row" justifyContent="flex-start" style={{ gap: 20 }} flex={1} mb={3}>
              {onImprove && (
                <Button variant="outlined" onClick={onImprove} >
                  Improve this Profile
                </Button>
              )}

              {onFlag && (
                <Button variant="outlined" onClick={onFlag} >
                  Flag this Profile
                </Button>
              )}
            </Box>
            {!AVAILABLE_FROM_CALENDAR && !DISABLE_CRRAL_CALENDAR ? (
              <FreelancerProfileCalendarSection freelancer={foundUser} />
            ) : !DISABLE_AVAILABILITY && (
              <>
                <FreelancerCommitmentSection freelancer={foundUser} />
                <FreelancerWorkStatusSection freelancer={foundUser} />
                <FreelancerProfileWLCalendarSection freelancer={foundUser} />
              </>
            )}
          </Box>
        </Box>
      ) : (
        <Typography variant="h5">
          <Typography variant="h5" component="a" href="/login">
            Sign in{' '}
          </Typography>
          to reveal {foundUser.firstName}'s availability and contact info
        </Typography>
      )}
    </Frame>
  )
}

export default FreelancerShowView
